import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { cn } from '@distribute/frontend/utils';

import { Helmet } from 'react-helmet-async';
import { authUserModel } from '../../../entities/auth-user';
import { pagesOverviewModel } from '../../../features/pages-overview';
import {
  DEFAULT_SORT_ITEMS,
  LoaderDots,
  SearchInput,
  SkeletonItem,
} from '../../../shared/ui';
import { Table } from './components/Table';
import { debounce } from 'lodash';

import { SelectSort } from '../../../shared/ui';
import { SelectMember, ALL_OWNERS } from '../../../features/team-members';
import { IconMap } from '../../../shared/sprite';

const blocks = [
  { id: 'totalPages', name: 'Total pages created' },
  { id: 'totalPageViews', name: 'Total page views' },
  { id: 'totalLeads', name: 'Total leads' },
];

const SORT_ITEMS = {
  lastViewed: { glyph: IconMap.Eye, label: 'Last viewed' },
  ...DEFAULT_SORT_ITEMS,
} as const;

export const PagesOverview: React.FC = () => {
  const dispatch = useDispatch();
  const user = useSelector(authUserModel.selectors.selectUser);

  const total: { [key: string]: number } = useSelector(
    pagesOverviewModel.selectors.selectTotal
  );

  const isDataLoading = useSelector(
    pagesOverviewModel.selectors.selectIsOverviewDataLoading
  );
  const isTotalLoading = useSelector(
    pagesOverviewModel.selectors.selectIsTotalLoading
  );
  const data = useSelector(
    pagesOverviewModel.selectors.selectPagesOverviewData
  );
  const meta = useSelector(
    pagesOverviewModel.selectors.selectPagesOverviewMeta
  );
  const search = useSelector(pagesOverviewModel.selectors.selectSearch);
  const orderBy = useSelector(pagesOverviewModel.selectors.selectOrderBy);
  const selectedFilterByUserId = useSelector(
    pagesOverviewModel.selectors.selectFilterByUserId
  );

  const onOwnerChange = (ownerId: string) => {
    const userId = ownerId === ALL_OWNERS ? undefined : ownerId;
    dispatch(pagesOverviewModel.actions.setFilterByUserId(userId));
    dispatch(pagesOverviewModel.actions.getPagesOverview({}));
  };

  const onSortChange = (sortId: string) => {
    dispatch(pagesOverviewModel.actions.setOrderBy(sortId));
    dispatch(pagesOverviewModel.actions.getPagesOverview({}));
  };

  const handleGetPagesOverview = useCallback(
    debounce(() => {
      dispatch(pagesOverviewModel.actions.getPagesOverview({}));
    }, 400),
    [dispatch]
  );

  const handlePageChange = (page: number) => {
    dispatch(pagesOverviewModel.actions.getPagesOverview({ page }));
  };

  const handlePageSearch = (value: string) => {
    dispatch(pagesOverviewModel.actions.setSearching(value));
    handleGetPagesOverview();
  };

  const formatNumber = (number?: number) => {
    if (!number) return '-';
    return new Intl.NumberFormat().format(number);
  };

  return (
    <>
      <Helmet titleTemplate="Overview - Distribute" />
      <div className="sm:-mx-1 min-w-0 flex flex-col flex-1 sm:gap-y-4 gap-y-6">
        <div className="flex flex-col gap-y-1 sm:py-4">
          <h1 className="font-medium text-gray-900 text-display-sm font-display">
            Welcome back, {user?.displayName} 👋
          </h1>
          <p className="text-gray-600 text-md">
            Your current team summary and activity.
          </p>
        </div>
        <section className="sm:mx-1 grid grid-cols-3 gap-4 sm:p-4 sm:border sm:border-gray-200 sm:rounded-xl">
          {blocks.map((block) => (
            <div
              key={block.id}
              className={cn(
                'border border-gray-200 rounded-xl p-6 flex flex-col gap-y-2',
                'sm:rounded-none sm:p-0 sm:border-y-0 sm:border-l-0 sm:border-r sm:last:border-r-0 sm:pr-4 sm:last:pr-0'
              )}
            >
              <p className="sm:text-xs text-sm text-gray-600 font-medium">
                {block.name}
              </p>
              {isTotalLoading ? (
                <SkeletonItem className="rounded-lg" height="38px" />
              ) : (
                <p className="sm:text-display-xs font-medium text-gray-900 truncate text-display-sm font-display tracking-[1px] sm:mt-auto">
                  {formatNumber(total[block.id])}
                </p>
              )}
            </div>
          ))}
        </section>
        <div className="flex flex-wrap justify-between items-center gap-3">
          <h2 className="font-display font-medium text-gray-900 text-display-xs">
            Pages
          </h2>
          <div className="flex flex-1 gap-3 justify-end md:hidden">
            <div className="w-full max-w-74 lg:max-w-48 flex-shrink-0">
              <SearchInput
                value={search}
                onChange={(value) => handlePageSearch(value)}
                placeholder="Search"
              />
            </div>
            <SelectMember
              value={selectedFilterByUserId}
              onChange={onOwnerChange}
            />
            <SelectSort
              value={orderBy}
              onChange={onSortChange}
              items={SORT_ITEMS}
            />
          </div>
        </div>
        <div
          className={cn('flex flex-col flex-grow-1', {
            'pb-6 md:pb-0': !isDataLoading,
          })}
        >
          {isDataLoading ? (
            <div className="grid place-items-center flex-grow-1">
              <LoaderDots />
            </div>
          ) : (
            <Table
              data={data}
              currentPage={meta.currentPage}
              totalPages={meta.totalPages}
              handleChangePage={handlePageChange}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default PagesOverview;
